import Icon from 'components/Icon';
import IconDocument from 'components/IconColor/32px/ml_document';
import Overlay from 'components/Layout/Overlay';
import SortableHandler from 'components/Sortable/SortableHandle';
import TooltipWrapper from 'components/Tooltip';
import {useTooltip} from 'components/Tooltip/hook';
import {
    ComponentProps,
    FC,
    ForwardedRef,
    forwardRef,
    MutableRefObject,
    useRef,
    useState,
} from 'react';
import {t} from 'ttag';
import {PermissionResponse} from 'types/permissions';
import {useModalMouseEscape} from 'utils/modal';

import SupersendPopover from '../../Popover';
import VerticalDots from '../VerticalDots';

const IconWithTooltip: FC<ComponentProps<typeof Icon>> = (props) => {
    const {setTooltipRef} = useTooltip();

    return <Icon ref={setTooltipRef} {...props} />;
};

const mergeRefs = <T = unknown,>(
    ...refs: (ForwardedRef<T> | MutableRefObject<T>)[]
) => {
    return (instance: T) => {
        if (!instance) return;
        refs.forEach((ref) => {
            if (typeof ref === 'function') {
                ref(instance);
            } else if (ref) {
                ref.current = instance;
            }
        });
    };
};

export type SupersendDocumentProps = {
    datePaid?: string;
    dateSent?: string;
    dateSentByPost?: string;
    dateViewed?: string;
    displayName: string;
    isEditing?: boolean;
    mid: string;
    permissions?: {
        canViewPdf: PermissionResponse;
    };
    popover?: Omit<ComponentProps<typeof SupersendPopover>, 'onClose'>;
    shouldDisplayNotViewed?: boolean;
    shouldDisplayNotYetSendbypost?: boolean;
    total?: string;
};

const SupersendDocument = forwardRef<HTMLDivElement, SupersendDocumentProps>(
    (
        {
            datePaid,
            dateSent,
            dateSentByPost,
            dateViewed,
            displayName,
            isEditing,
            mid,
            popover,
            shouldDisplayNotViewed,
            shouldDisplayNotYetSendbypost,
            total,
        },
        ref,
    ) => {
        const [isPopupOpen, setIsPopupOpen] = useState(false);
        const elementRef = useRef<HTMLDivElement>(null);
        useModalMouseEscape({
            enabled: true,
            exclude: [elementRef],
            isOpen: isPopupOpen,
            onClose: () => setIsPopupOpen(false),
        });

        return (
            <>
                <div
                    className="flex w-fit items-start gap-1 rounded border border-grey-400 bg-grey-100 p-2"
                    onClick={() => setIsPopupOpen(!isPopupOpen)}
                    onKeyDown={() => {}}
                    ref={mergeRefs(ref, elementRef)}
                    role="button"
                    tabIndex={0}
                >
                    <div className="flex h-10 items-center gap-1">
                        {isEditing && (
                            <SortableHandler id={mid}>
                                <VerticalDots />
                            </SortableHandler>
                        )}
                        <IconDocument />
                    </div>
                    <div className="flex flex-col text-sm">
                        <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-0.5">
                                {displayName && <span>{displayName}</span>}
                                {datePaid && (
                                    <TooltipWrapper
                                        message={t`Paid: ${datePaid}`}
                                    >
                                        <IconWithTooltip
                                            icon="enabled_checkbox"
                                            id="datePaid"
                                            size="base"
                                        />
                                    </TooltipWrapper>
                                )}
                                {dateSent && (
                                    <TooltipWrapper
                                        message={t`Sent: ${dateSent}`}
                                    >
                                        <IconWithTooltip
                                            icon="send_complete"
                                            id="dateSent"
                                            size="base"
                                        />
                                    </TooltipWrapper>
                                )}
                                {dateViewed && (
                                    <TooltipWrapper
                                        message={t`Viewed: ${dateViewed}`}
                                    >
                                        <IconWithTooltip
                                            icon="show"
                                            id="dateViewed"
                                            size="base"
                                        />
                                    </TooltipWrapper>
                                )}
                                {shouldDisplayNotViewed && (
                                    <TooltipWrapper message={t`Not Viewed`}>
                                        <IconWithTooltip
                                            icon="hide"
                                            id="notViewed"
                                            size="base"
                                        />
                                    </TooltipWrapper>
                                )}
                                {dateSentByPost && (
                                    <TooltipWrapper
                                        message={t`Sent by Japan Post: ${dateSentByPost}`}
                                    >
                                        <IconWithTooltip
                                            icon="mail_point"
                                            id="dateSentByPost"
                                            size="base"
                                        />
                                    </TooltipWrapper>
                                )}
                                {shouldDisplayNotYetSendbypost && (
                                    <TooltipWrapper message={t`Not Yet Sent`}>
                                        <IconWithTooltip
                                            icon="mail_point"
                                            id="notYetSendByPost"
                                            size="base"
                                        />
                                    </TooltipWrapper>
                                )}
                            </span>
                        </div>
                        {total && <span>{t`Total : ${total}`}</span>}
                    </div>
                </div>
                {popover && isPopupOpen && (
                    <Overlay
                        className="clickable"
                        element={elementRef.current}
                        layoutLayer="z-modal"
                        layoutMethod="bottom"
                        layoutOffset={4}
                    >
                        <SupersendPopover
                            {...popover}
                            onClose={() => setIsPopupOpen(false)}
                        />
                    </Overlay>
                )}
            </>
        );
    },
);
SupersendDocument.displayName = 'SupersendDocument';
export default SupersendDocument;
